import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { apiUrl } from 'src/constants/config';
import ReduxService from 'src/redux/redux.service';
import { ReduxState } from 'src/redux/redux-state';
import {
  SurveysApiErrorEnvelope,
  SurveysApiSuccessEnvelope,
} from './surveys-api-v1.types';

/** Derive `/api/v1` from `VITE_API_URL` which ends in `/v1`. */
export const getSurveysApiV1BaseUrl = (): string => {
  const trimmed = (apiUrl || '').replace(/\/+$/, '');
  if (trimmed.endsWith('/v1')) {
    return `${trimmed.slice(0, -3)}/api/v1`;
  }
  return `${trimmed}/api/v1`;
};

const getAccessToken = (): string | undefined => {
  const state = ReduxService.Store.getState() as ReduxState;
  return state?.userState?.access || undefined;
};

export type SurveysApiV1RequestError = {
  status?: number;
  message: string;
  code?: string;
};

const toRequestError = (error: unknown): SurveysApiV1RequestError => {
  const axiosError = error as AxiosError<SurveysApiErrorEnvelope>;
  const status = axiosError.response?.status;
  const body = axiosError.response?.data;
  const code = body?.error?.code;
  const message =
    body?.error?.message ||
    axiosError.message ||
    'Surveys API request failed';
  return { status, message, code };
};

export async function surveysApiV1Request<T>(
  config: AxiosRequestConfig
): Promise<T> {
  const access = getAccessToken();
  try {
    const response = await axios({
      ...config,
      headers: {
        ...(config.headers || {}),
        ...(access ? { Authorization: `Bearer ${access}` } : {}),
      },
    });
    return response.data as T;
  } catch (error) {
    throw toRequestError(error);
  }
}

export async function surveysApiV1Envelope<T>(
  config: AxiosRequestConfig
): Promise<SurveysApiSuccessEnvelope<T>> {
  return surveysApiV1Request<SurveysApiSuccessEnvelope<T>>(config);
}

export function withExtClientId(
  params: Record<string, string | number | undefined | null> | undefined,
  extClientId?: string
): Record<string, string | number> | undefined {
  const next: Record<string, string | number> = {};
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        next[key] = value;
      }
    }
  }
  if (extClientId) {
    next.ext_client_id = extClientId;
  }
  return Object.keys(next).length ? next : undefined;
}
